import { useQuery } from '@tanstack/react-query';
import type { AppErrorRowDTO, PlatformOverview } from '@bifeai/shared';
import { Badge, Card, CardBody, CardHeader, EmptyState, Kpi, PageHeader, Skeleton } from '@bifeai/ui';
import { Bug, CalendarClock, CircleCheck, Store } from 'lucide-react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { formatDate, timeAgo } from '../lib/format';

function uptime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

export function OverviewPage() {
  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get<PlatformOverview>('/platform/overview'),
    refetchInterval: 60_000,
  });
  const errors = useQuery({ queryKey: ['errors', 'open'], queryFn: () => api.get<AppErrorRowDTO[]>('/platform/errors?status=open') });
  const d = overview.data;

  return (
    <>
      <PageHeader title="Inicio" subtitle="Estado de BifeAI: clientes, licencias y errores de la aplicación." />
      <Card>
        <CardHeader title="Resumen" subtitle="Se actualiza solo cada minuto" />
        <CardBody>
          {!d ? (
            <div className="bf-kpi-grid">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} style={{ height: 116 }} />
              ))}
            </div>
          ) : (
            <div className="bf-kpi-grid">
              <Kpi label="Clientes" icon={<Store />} value={d.tenants.total} delta={`${d.tenants.active} con licencia activa`} deltaMuted />
              <Kpi
                label="Solo lectura o suspendidos"
                tone="amber"
                icon={<CalendarClock />}
                value={d.tenants.readOnly + d.tenants.suspended}
                delta={`${d.tenants.readOnly} solo lectura, ${d.tenants.suspended} suspendidos`}
                deltaMuted
              />
              <Kpi
                label="Errores abiertos"
                tone="red"
                icon={<Bug />}
                value={d.errors.open}
                delta={`${d.errors.newLast24h} nuevos en 24 h`}
                deltaMuted
              />
              <Kpi
                label="Sistema"
                tone="green"
                icon={<CircleCheck />}
                value={d.system.database === 'ok' ? 'En línea' : 'Con fallas'}
                delta={`Versión ${d.system.release ?? 'sin dato'}, activo hace ${uptime(d.system.uptimeSeconds)}`}
                deltaMuted
              />
            </div>
          )}
        </CardBody>
      </Card>

      <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
        <Card>
          <CardHeader title="Abonos por vencer" subtitle="Próximos 30 días" actions={<Link to="/clientes">Ver clientes</Link>} />
          <CardBody flush>
            {d?.licensesExpiringSoon.length === 0 && (
              <EmptyState icon={<CalendarClock />} title="Nada por vencer" text="Ningún abono anual vence en los próximos 30 días." />
            )}
            {d?.licensesExpiringSoon.map((t) => (
              <div key={t.id} className="bf-notif" style={{ cursor: 'default' }}>
                <span className="bf-notif-dot bf-notif-dot--WARNING" />
                <div style={{ flex: 1 }}>
                  <p className="bf-notif-title">{t.name}</p>
                  <p className="bf-notif-body">Vence el {formatDate(t.serviceUntil)}</p>
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Errores abiertos" subtitle="Los más recientes primero" actions={<Link to="/errores">Ver todos</Link>} />
          <CardBody flush>
            {errors.data?.length === 0 && <EmptyState icon={<CircleCheck />} title="Sin errores abiertos" text="Todo funciona como debe." />}
            {errors.data?.slice(0, 5).map((e) => (
              <div key={e.id} className="bf-notif" style={{ cursor: 'default' }}>
                <span className={`bf-notif-dot bf-notif-dot--${e.status === 'NEW' ? 'CRITICAL' : 'WARNING'}`} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="bf-notif-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.message}
                  </p>
                  <p className="bf-notif-body">
                    {e.count} {e.count === 1 ? 'vez' : 'veces'}, último {timeAgo(e.lastSeen)}
                  </p>
                </div>
                <Badge>{e.source === 'api' ? 'API' : e.source === 'web' ? 'App' : 'Admin'}</Badge>
              </div>
            ))}
          </CardBody>
        </Card>
      </div>
    </>
  );
}
